import type { ReactNode } from 'react'

/**
 * Icons for the rail.
 *
 * They exist because of the FOLDED rail: at 56px there is no room for
 * "Übergabeprotokolle", and a rail with nothing in it is not navigation. Drawn
 * by hand in the theme toggle's idiom — a 16px grid, a 1.4 stroke, no fill,
 * `currentColor` — rather than pulled from an icon package: ten glyphs do not
 * justify a dependency, and a second stroke weight next to the theme control
 * is the kind of mismatch that reads as two products.
 */

export type NavIconName =
  | 'checklists'
  | 'handovers'
  | 'briefings'
  | 'imageAi'
  | 'dashboard'
  | 'system'
  | 'settings'
  | 'panel'
  | 'menu'
  | 'close'

const GLYPHS: Record<NavIconName, ReactNode> = {
  // Two ticked lines and one still open: the document checklist in miniature.
  checklists: (
    <path d="M2.2 3.9l1.2 1.2 2.1-2.3M8 4h5.8M2.2 8.4l1.2 1.2 2.1-2.3M8 8.5h5.8M2.6 12.9h2.5M8 13h5.8" />
  ),
  // A key: the thing that physically changes hands at an Übergabe.
  handovers: (
    <>
      <circle cx="5" cy="11" r="2.7" />
      <path d="M6.9 9.1l6.8-6.8M11.3 4.7l2 2M9.3 6.7l1.6 1.6" />
    </>
  ),
  // A sheet with lines under a magnifier: documents read and evaluated.
  briefings: (
    <>
      <path d="M9.6 1.8H3.4a1.2 1.2 0 0 0-1.2 1.2v10a1.2 1.2 0 0 0 1.2 1.2h3.4" />
      <path d="M4.6 5h4.6M4.6 7.8h2.6" />
      <circle cx="10.6" cy="10" r="2.6" />
      <path d="M12.5 11.9l1.8 1.8" />
    </>
  ),
  imageAi: (
    <>
      <rect x="1.8" y="2.8" width="12.4" height="10.4" rx="1.2" />
      <circle cx="5.5" cy="6.3" r="1.1" />
      <path d="M2.2 11.6l3.4-3.2 2.6 2.4 2.1-1.9 3.5 3.2" />
    </>
  ),
  dashboard: (
    <>
      <rect x="2" y="2" width="5" height="6.4" rx="0.8" />
      <rect x="9" y="2" width="5" height="3.6" rx="0.8" />
      <rect x="9" y="7.6" width="5" height="6.4" rx="0.8" />
      <rect x="2" y="10.4" width="5" height="3.6" rx="0.8" />
    </>
  ),
  // A pulse — this page is the live health check.
  system: <path d="M1.4 8.2h2.9l1.9-4.9 3.2 9.4 1.9-4.5h3.3" />,
  settings: (
    <>
      <path d="M1.8 4.6h6M11.4 4.6h2.8M1.8 11.4h2.8M8 11.4h6.2" />
      <circle cx="9.7" cy="4.6" r="1.7" />
      <circle cx="6.3" cy="11.4" r="1.7" />
    </>
  ),
  // A window with its side panel marked off: the fold control.
  panel: (
    <>
      <rect x="1.8" y="2.8" width="12.4" height="10.4" rx="1.2" />
      <path d="M6 2.8v10.4" />
    </>
  ),
  menu: <path d="M2.2 4.4h11.6M2.2 8h11.6M2.2 11.6h11.6" />,
  close: <path d="M3.8 3.8l8.4 8.4M12.2 3.8l-8.4 8.4" />,
}

export function NavIcon({ name, className = 'size-4' }: { name: NavIconName; className?: string }) {
  return (
    <svg
      viewBox="0 0 16 16"
      className={`${className} shrink-0`}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      {GLYPHS[name]}
    </svg>
  )
}
